export const GALAGA_FRAME_RATE = 60;
export const GALAGA_BOUNDS_X = 14;
export const GALAGA_BOUNDS_Y = 9;
export const GALAGA_BOUNDS_Z = 5.2;
const GALAGA_INTRO_INVULNERABLE_FRAMES = 480;
const GALAGA_FIRST_DIVE_FRAME = 420;
const GALAGA_FIRST_ENEMY_SHOT_FRAME = 360;
const GALAGA_COMBO_FRAMES = 150;
const GALAGA_WEAPON_ORDER: GalagaWeapon[] = ["beam", "arc", "ray"];

export type GalagaStatus = "playing" | "won" | "lost";
export type GalagaWeapon = "beam" | "arc" | "ray";
export type GalagaView = "third" | "cockpit";
export type GalagaEnemyKind = "scout" | "striker" | "bomber" | "ace";

export type GalagaInputState = {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  forward: boolean;
  back: boolean;
  boost: boolean;
};

export type GalagaPlayer = {
  x: number;
  y: number;
  z: number;
  roll: number;
  cooldownFrames: number;
  invulnerableFrames: number;
  boostMeter: number;
  boosting: boolean;
  weapon: GalagaWeapon;
  view: GalagaView;
};

export type GalagaEnemy = {
  id: number;
  kind: GalagaEnemyKind;
  row: number;
  column: number;
  x: number;
  y: number;
  z: number;
  baseX: number;
  baseY: number;
  baseZ: number;
  phase: number;
  health: number;
  maxHealth: number;
  radius: number;
  scoreValue: number;
  diving: boolean;
  diveFrames: number;
};

export type GalagaShot = {
  id: number;
  x: number;
  y: number;
  z: number;
  velocityX: number;
  velocityY: number;
  velocityZ: number;
  radius: number;
  damage: number;
  ageFrames: number;
  lifeFrames: number;
  friendly: boolean;
  weapon?: GalagaWeapon;
  pierces?: boolean;
  hitEnemyIds: number[];
};

export type GalagaExplosion = {
  id: number;
  x: number;
  y: number;
  z: number;
  ageFrames: number;
  lifeFrames: number;
};

export type GalagaState = {
  status: GalagaStatus;
  frame: number;
  frameAccumulator: number;
  lastElapsed: number | null;
  score: number;
  lives: number;
  wave: number;
  combo: number;
  comboFrames: number;
  nextId: number;
  player: GalagaPlayer;
  enemies: GalagaEnemy[];
  shots: GalagaShot[];
  enemyShots: GalagaShot[];
  explosions: GalagaExplosion[];
  message: string;
};

export function createGalagaInputState(): GalagaInputState {
  return {
    left: false,
    right: false,
    up: false,
    down: false,
    forward: false,
    back: false,
    boost: false,
  };
}

export function createGalagaState(): GalagaState {
  const state: GalagaState = {
    status: "playing",
    frame: 0,
    frameAccumulator: 0,
    lastElapsed: null,
    score: 0,
    lives: 3,
    wave: 1,
    combo: 0,
    comboFrames: 0,
    nextId: 1,
    player: {
      x: 0,
      y: -7.2,
      z: 0,
      roll: 0,
      cooldownFrames: 0,
      invulnerableFrames: GALAGA_INTRO_INVULNERABLE_FRAMES,
      boostMeter: 100,
      boosting: false,
      weapon: "beam",
      view: "third",
    },
    enemies: [],
    shots: [],
    enemyShots: [],
    explosions: [],
    message: "Beam ready",
  };
  spawnGalagaWave(state);
  return state;
}

export function advanceGalagaElapsed(state: GalagaState, input: GalagaInputState, elapsed: number) {
  if (state.lastElapsed === null) {
    state.lastElapsed = elapsed;
    return;
  }

  const deltaSeconds = clamp(elapsed - state.lastElapsed, 0, 2);
  state.lastElapsed = elapsed;
  state.frameAccumulator += deltaSeconds * GALAGA_FRAME_RATE;
  const frames = Math.min(240, Math.floor(state.frameAccumulator));
  state.frameAccumulator -= frames;

  for (let frame = 0; frame < frames; frame += 1) {
    advanceGalagaFrame(state, input);
  }
}

export function advanceGalagaFrame(state: GalagaState, input: GalagaInputState) {
  state.frame += 1;
  advanceExplosions(state);
  advanceCombo(state);

  if (state.status !== "playing") {
    return;
  }

  advancePlayer(state, input);
  advanceEnemies(state);
  advanceShots(state);
  checkGalagaCollisions(state);

  if (state.enemies.length === 0) {
    state.wave += 1;
    const clearBonus = 500 + state.wave * 150;
    state.score += clearBonus;
    state.message = `Wave ${state.wave}`;
    spawnGalagaWave(state);
  }
}

export function fireGalagaShot(state: GalagaState) {
  if (state.status !== "playing" || state.player.cooldownFrames > 0) {
    return false;
  }

  const shots = createPlayerShots(state);
  state.shots.push(...shots);
  if (state.shots.length > 34) {
    state.shots.splice(0, state.shots.length - 34);
  }
  state.player.cooldownFrames = getWeaponCooldown(state.player.weapon, state.player.boosting);
  state.message = `${getWeaponLabel(state.player.weapon)} fire`;
  return true;
}

export function cycleGalagaWeapon(state: GalagaState) {
  const index = GALAGA_WEAPON_ORDER.indexOf(state.player.weapon);
  state.player.weapon = GALAGA_WEAPON_ORDER[(index + 1) % GALAGA_WEAPON_ORDER.length];
  state.player.cooldownFrames = Math.min(state.player.cooldownFrames, 8);
  state.message = `${getWeaponLabel(state.player.weapon)} ready`;
  return state.player.weapon;
}

export function toggleGalagaView(state: GalagaState) {
  state.player.view = state.player.view === "third" ? "cockpit" : "third";
  state.message = state.player.view === "cockpit" ? "Cockpit view" : "Third view";
  return state.player.view;
}

export function setGalagaPlayerFromPointer(state: GalagaState, normalizedX: number, normalizedY: number) {
  state.player.x = clamp(normalizedX * GALAGA_BOUNDS_X, -GALAGA_BOUNDS_X, GALAGA_BOUNDS_X);
  state.player.y = clamp(-normalizedY * GALAGA_BOUNDS_Y, -GALAGA_BOUNDS_Y, GALAGA_BOUNDS_Y);
}

export function serializeGalagaState(state: GalagaState) {
  return {
    status: state.status,
    coordinateSystem: "origin is screen center; x increases right; y increases up; z increases toward the camera/player",
    frame: state.frame,
    score: state.score,
    lives: state.lives,
    wave: state.wave,
    combo: state.combo,
    comboFrames: state.comboFrames,
    message: state.message,
    enemyCount: state.enemies.length,
    shotCount: state.shots.length,
    enemyShotCount: state.enemyShots.length,
    player: {
      x: round(state.player.x),
      y: round(state.player.y),
      z: round(state.player.z),
      roll: round(state.player.roll),
      cooldownFrames: state.player.cooldownFrames,
      invulnerableFrames: state.player.invulnerableFrames,
      boostMeter: round(state.player.boostMeter),
      boosting: state.player.boosting,
      weapon: state.player.weapon,
      view: state.player.view,
    },
    enemies: state.enemies.slice(0, 12).map((enemy) => ({
      id: enemy.id,
      kind: enemy.kind,
      x: round(enemy.x),
      y: round(enemy.y),
      z: round(enemy.z),
      health: enemy.health,
      maxHealth: enemy.maxHealth,
      diving: enemy.diving,
    })),
    shots: state.shots.slice(0, 8).map((shot) => ({
      id: shot.id,
      x: round(shot.x),
      y: round(shot.y),
      z: round(shot.z),
      weapon: shot.weapon,
    })),
    enemyShots: state.enemyShots
      .slice(0, 8)
      .map((shot) => ({ id: shot.id, x: round(shot.x), y: round(shot.y), z: round(shot.z) })),
    explosions: state.explosions.length,
  };
}

function spawnGalagaWave(state: GalagaState) {
  const rows = Math.min(4, 3 + Math.floor((state.wave - 1) / 3));
  const columns = state.wave >= 2 ? 6 : 5;
  const spacing = columns > 5 ? 3.2 : 3.8;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const kind = getEnemyKind(state.wave, row, column, rows);
      const stats = getEnemyStats(kind, state.wave);
      const baseX = (column - (columns - 1) / 2) * spacing;
      const baseY = 4.9 + row * 1.55;
      const baseZ = -0.4 - row * 1.28 + (column % 2 === 0 ? 0.42 : -0.42);
      state.enemies.push({
        id: state.nextId,
        kind,
        row,
        column,
        x: baseX,
        y: baseY,
        z: baseZ,
        baseX,
        baseY,
        baseZ,
        phase: state.nextId * 0.74,
        health: stats.health,
        maxHealth: stats.health,
        radius: stats.radius,
        scoreValue: stats.scoreValue,
        diving: false,
        diveFrames: 0,
      });
      state.nextId += 1;
    }
  }
}

function advancePlayer(state: GalagaState, input: GalagaInputState) {
  const dx = (input.right ? 1 : 0) - (input.left ? 1 : 0);
  const dy = (input.up ? 1 : 0) - (input.down ? 1 : 0);
  const dz = (input.forward ? 1 : 0) - (input.back ? 1 : 0);
  const distance = Math.hypot(dx, dy, dz) || 1;
  state.player.boosting = input.boost && state.player.boostMeter > 1;
  const speed = state.player.boosting ? 0.58 : 0.3;
  state.player.x = clamp(state.player.x + (dx / distance) * speed, -GALAGA_BOUNDS_X, GALAGA_BOUNDS_X);
  state.player.y = clamp(state.player.y + (dy / distance) * speed, -GALAGA_BOUNDS_Y, GALAGA_BOUNDS_Y);
  state.player.z = clamp(state.player.z + (dz / distance) * speed, -GALAGA_BOUNDS_Z, GALAGA_BOUNDS_Z);
  state.player.roll += (dx * (state.player.boosting ? 0.72 : 0.46) - state.player.roll) * 0.18;
  state.player.boostMeter = clamp(state.player.boostMeter + (state.player.boosting ? -1.45 : 0.38), 0, 100);
  state.player.cooldownFrames = Math.max(0, state.player.cooldownFrames - (state.player.boosting ? 2 : 1));
  state.player.invulnerableFrames = Math.max(0, state.player.invulnerableFrames - 1);
}

function advanceEnemies(state: GalagaState) {
  if (
    state.frame > GALAGA_FIRST_DIVE_FRAME &&
    state.frame % Math.max(52, 132 - state.wave * 8) === 0 &&
    state.enemies.length > 0
  ) {
    const idleEnemies = state.enemies.filter((enemy) => !enemy.diving);
    if (idleEnemies.length === 0) {
      return;
    }
    const diver = idleEnemies[(Math.floor(state.frame / 90) + state.wave) % idleEnemies.length];
    diver.diving = true;
    diver.diveFrames = 0;
    state.message = `${getEnemyLabel(diver.kind)} diving`;
  }

  if (state.wave >= 4 && state.frame > GALAGA_FIRST_DIVE_FRAME && state.frame % 96 === 0) {
    const escort = state.enemies.find((enemy) => !enemy.diving && enemy.kind === "scout");
    if (escort) {
      escort.diving = true;
      escort.diveFrames = 0;
    }
  }

  state.enemies.forEach((enemy) => {
    const sway = Math.sin(state.frame * (0.027 + state.wave * 0.0025) + enemy.phase);
    const formationDrift = Math.sin(state.frame * 0.009 + state.wave) * Math.min(2.1, 0.8 + state.wave * 0.16);
    if (enemy.diving) {
      enemy.diveFrames += 1;
      const aggression = enemy.kind === "ace" ? 1.28 : enemy.kind === "striker" ? 1.16 : 1;
      enemy.y -= (0.19 + state.wave * 0.018) * aggression;
      enemy.x += (state.player.x + Math.sin(enemy.diveFrames * 0.09 + enemy.phase) * 1.6 - enemy.x) * 0.028 * aggression;
      enemy.z += (state.player.z + Math.cos(enemy.diveFrames * 0.07 + enemy.phase) * 1.8 - enemy.z) * 0.03 * aggression;
      if (enemy.y < -GALAGA_BOUNDS_Y - 1.5) {
        enemy.diving = false;
        enemy.diveFrames = 0;
        enemy.x = enemy.baseX;
        enemy.y = enemy.baseY;
        enemy.z = enemy.baseZ;
      }
    } else {
      enemy.x = enemy.baseX + formationDrift + sway * (1.1 + state.wave * 0.1);
      enemy.y = enemy.baseY + Math.cos(state.frame * 0.02 + enemy.phase) * 0.42 - Math.min(2.4, state.wave * 0.14);
      enemy.z = enemy.baseZ + Math.sin(state.frame * 0.02 + enemy.phase) * (0.7 + enemy.row * 0.16);
    }

    if (
      state.frame > GALAGA_FIRST_ENEMY_SHOT_FRAME &&
      state.enemyShots.length < 32 &&
      (state.frame + enemy.id * 19) % Math.max(58, 178 - state.wave * 13) === 0
    ) {
      fireEnemyShot(state, enemy);
      if (enemy.kind === "bomber" && state.enemyShots.length < 32) {
        fireEnemyShot(state, enemy, -0.18);
        fireEnemyShot(state, enemy, 0.18);
      }
    }
  });
}

function advanceShots(state: GalagaState) {
  state.shots.forEach((shot) => {
    shot.x += shot.velocityX;
    shot.y += shot.velocityY;
    shot.z += shot.velocityZ;
    shot.ageFrames += 1;
  });
  state.enemyShots.forEach((shot) => {
    shot.x += shot.velocityX;
    shot.y += shot.velocityY;
    shot.z += shot.velocityZ;
    shot.ageFrames += 1;
  });
  state.shots = state.shots.filter((shot) => shot.y < GALAGA_BOUNDS_Y + 3 && shot.ageFrames < shot.lifeFrames);
  state.enemyShots = state.enemyShots.filter((shot) => shot.y > -GALAGA_BOUNDS_Y - 3 && shot.ageFrames < shot.lifeFrames);
}

function checkGalagaCollisions(state: GalagaState) {
  const killedEnemies = new Set<number>();
  const spentShots = new Set<number>();

  state.shots.forEach((shot) => {
    state.enemies.forEach((enemy) => {
      if (killedEnemies.has(enemy.id) || shot.hitEnemyIds.includes(enemy.id) || distance3(shot, enemy) > shot.radius + enemy.radius) {
        return;
      }
      enemy.health -= shot.damage;
      shot.hitEnemyIds.push(enemy.id);
      if (!shot.pierces) {
        spentShots.add(shot.id);
      }
      if (enemy.health <= 0) {
        killedEnemies.add(enemy.id);
        state.combo += 1;
        state.comboFrames = GALAGA_COMBO_FRAMES;
        const score = enemy.scoreValue + state.wave * 30 + Math.max(0, state.combo - 1) * 25;
        state.score += score;
        state.message = `${getEnemyLabel(enemy.kind)} +${score}`;
        addExplosion(state, enemy.x, enemy.y, enemy.z);
      }
    });
  });

  state.shots = state.shots.filter((shot) => !spentShots.has(shot.id));
  state.enemies = state.enemies.filter((enemy) => !killedEnemies.has(enemy.id));

  if (state.player.invulnerableFrames > 0) {
    return;
  }

  const hitByShot = state.enemyShots.find((shot) => distance3(shot, state.player) < shot.radius + 0.76);
  const hitByEnemy = state.enemies.find((enemy) => distance3(enemy, state.player) < enemy.radius + 0.72);
  if (!hitByShot && !hitByEnemy) {
    return;
  }

  if (hitByShot) {
    state.enemyShots = state.enemyShots.filter((shot) => shot.id !== hitByShot.id);
  }
  if (hitByEnemy) {
    state.enemies = state.enemies.filter((enemy) => enemy.id !== hitByEnemy.id);
  }
  addExplosion(state, state.player.x, state.player.y, state.player.z);
  state.lives -= 1;
  state.combo = 0;
  state.comboFrames = 0;
  state.player.x = 0;
  state.player.y = -7.2;
  state.player.z = 0;
  state.player.roll = 0;
  state.player.invulnerableFrames = 150;
  state.message = state.lives > 0 ? `Lives ${state.lives}` : "Game over";
  if (state.lives <= 0) {
    state.status = "lost";
  }
}

function advanceExplosions(state: GalagaState) {
  state.explosions.forEach((explosion) => {
    explosion.ageFrames += 1;
  });
  state.explosions = state.explosions.filter((explosion) => explosion.ageFrames < explosion.lifeFrames);
}

function addExplosion(state: GalagaState, x: number, y: number, z: number) {
  state.explosions.push({
    id: state.nextId,
    x,
    y,
    z,
    ageFrames: 0,
    lifeFrames: 38,
  });
  state.nextId += 1;
}

function advanceCombo(state: GalagaState) {
  if (state.comboFrames > 0) {
    state.comboFrames -= 1;
    return;
  }
  state.combo = 0;
}

function createPlayerShots(state: GalagaState): GalagaShot[] {
  const start = {
    x: state.player.x,
    y: state.player.y + 1.2,
    z: state.player.z - 0.24,
  };

  if (state.player.weapon === "arc") {
    return [-0.25, 0, 0.25].map((spread) =>
      createFriendlyShot(state, {
        ...start,
        velocityX: spread,
        velocityY: 0.74,
        velocityZ: -0.2 - Math.abs(spread) * 0.28,
        radius: 0.72,
        damage: 1,
        lifeFrames: 68,
        weapon: "arc",
      }),
    );
  }

  if (state.player.weapon === "ray") {
    return [
      createFriendlyShot(state, {
        ...start,
        velocityX: 0,
        velocityY: 1.05,
        velocityZ: -0.42,
        radius: 0.62,
        damage: 3,
        lifeFrames: 46,
        weapon: "ray",
        pierces: true,
      }),
    ];
  }

  return [
    createFriendlyShot(state, {
      ...start,
      velocityX: 0,
      velocityY: 0.86,
      velocityZ: -0.24,
      radius: 0.82,
      damage: 1,
      lifeFrames: 62,
      weapon: "beam",
    }),
  ];
}

function createFriendlyShot(
  state: GalagaState,
  shot: Pick<GalagaShot, "x" | "y" | "z" | "velocityX" | "velocityY" | "velocityZ" | "radius" | "damage" | "lifeFrames" | "weapon"> &
    Partial<Pick<GalagaShot, "pierces">>,
): GalagaShot {
  const nextShot: GalagaShot = {
    id: state.nextId,
    ...shot,
    ageFrames: 0,
    friendly: true,
    hitEnemyIds: [],
  };
  state.nextId += 1;
  return nextShot;
}

function fireEnemyShot(state: GalagaState, enemy: GalagaEnemy, spreadX = 0) {
  const dx = state.player.x - enemy.x + spreadX * 4;
  const dy = state.player.y - enemy.y;
  const dz = state.player.z - enemy.z;
  const distance = Math.hypot(dx, dy, dz) || 1;
  const speed = (enemy.kind === "ace" ? 0.38 : enemy.kind === "striker" ? 0.34 : 0.3) + state.wave * 0.012;
  state.enemyShots.push({
    id: state.nextId,
    x: enemy.x,
    y: enemy.y - 0.7,
    z: enemy.z,
    velocityX: (dx / distance) * speed,
    velocityY: (dy / distance) * speed,
    velocityZ: (dz / distance) * speed,
    radius: enemy.kind === "bomber" ? 0.38 : 0.28,
    damage: 1,
    ageFrames: 0,
    lifeFrames: 120,
    friendly: false,
    hitEnemyIds: [],
  });
  state.nextId += 1;
}

function getEnemyKind(wave: number, row: number, column: number, rows: number): GalagaEnemyKind {
  if (wave >= 3 && row === rows - 1 && column % 3 === 1) {
    return "bomber";
  }
  if (wave >= 2 && (row + column + wave) % 5 === 0) {
    return "striker";
  }
  if (wave >= 4 && row === 0 && column % 4 === 0) {
    return "ace";
  }
  return "scout";
}

function getEnemyStats(kind: GalagaEnemyKind, wave: number) {
  const armor = Math.floor((wave - 1) / 4);
  if (kind === "ace") {
    return { health: 3 + armor, radius: 1.08, scoreValue: 240 };
  }
  if (kind === "bomber") {
    return { health: 3 + armor, radius: 1.14, scoreValue: 220 };
  }
  if (kind === "striker") {
    return { health: 2 + armor, radius: 0.98, scoreValue: 160 };
  }
  return { health: 1 + armor, radius: 0.9, scoreValue: 100 };
}

function getWeaponCooldown(weapon: GalagaWeapon, boosting: boolean) {
  const multiplier = boosting ? 0.72 : 1;
  if (weapon === "arc") {
    return Math.round(22 * multiplier);
  }
  if (weapon === "ray") {
    return Math.round(30 * multiplier);
  }
  return Math.round(10 * multiplier);
}

function getWeaponLabel(weapon: GalagaWeapon) {
  if (weapon === "arc") {
    return "Arc";
  }
  if (weapon === "ray") {
    return "Ray";
  }
  return "Beam";
}

function getEnemyLabel(kind: GalagaEnemyKind) {
  if (kind === "ace") {
    return "Ace";
  }
  if (kind === "bomber") {
    return "Bomber";
  }
  if (kind === "striker") {
    return "Striker";
  }
  return "Scout";
}

function distance3(
  left: { x: number; y: number; z: number },
  right: { x: number; y: number; z: number },
) {
  return Math.hypot(left.x - right.x, left.y - right.y, left.z - right.z);
}

function clamp(value: number, minValue: number, maxValue: number) {
  return Math.max(minValue, Math.min(maxValue, value));
}

function round(value: number) {
  return Number(value.toFixed(2));
}
